import { Link } from 'react-router';
import { Trophy, Plus, Clock, TrendingUp, AlertTriangle, CheckCircle, Medal } from 'lucide-react';
import { useChallenges } from '@/hooks/use-challenges';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import type { Challenge } from '@/types/api';

const statusConfig: Record<string, { badgeColor: 'green' | 'red' | 'blue' | 'default'; icon: typeof Trophy; label: string }> = {
  PENDING: { badgeColor: 'default', icon: Clock, label: 'Pending' },
  ACTIVE: { badgeColor: 'blue', icon: TrendingUp, label: 'Active' },
  PASSED: { badgeColor: 'green', icon: CheckCircle, label: 'Passed' },
  FAILED: { badgeColor: 'red', icon: AlertTriangle, label: 'Failed' },
  EXPIRED: { badgeColor: 'default', icon: Clock, label: 'Expired' },
};

function ChallengeCard({ challenge }: { challenge: Challenge }) {
  const config = statusConfig[challenge.status] ?? statusConfig['PENDING']!;
  const StatusIcon = config.icon;
  const metrics = challenge.metrics;

  return (
    <Link to={`/challenges/${challenge.id}`} className="block no-underline">
      <div className="glass rounded-xl p-5 hover:border-orange/30 transition-all duration-200 cursor-pointer">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-orange" />
            <span className="text-sm font-bold">{challenge.tier} Challenge</span>
          </div>
          <Badge color={config.badgeColor}>
            <StatusIcon size={12} className="mr-1" />
            {config.label}
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <div className="text-[10px] text-ink-4 uppercase tracking-wider">Capital</div>
            <div className="text-sm font-bold">${challenge.starting_capital.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] text-ink-4 uppercase tracking-wider">Target</div>
            <div className="text-sm font-bold">{challenge.profit_target_pct}%</div>
          </div>
          <div>
            <div className="text-[10px] text-ink-4 uppercase tracking-wider">Reward</div>
            <div className="text-sm font-bold text-orange">${challenge.reward_amount}</div>
          </div>
        </div>

        {metrics && challenge.status === 'ACTIVE' && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-ink-3">Progress</span>
              <span className="font-bold">{metrics.progress_to_target_pct.toFixed(1)}%</span>
            </div>
            <div className="w-full h-1.5 bg-surface rounded-full mt-1.5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(100, Math.max(0, metrics.progress_to_target_pct))}%`,
                  background: 'linear-gradient(90deg, #f97316, #f59e0b)',
                }}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-[10px] text-ink-4">
              <span>Return: {metrics.return_pct.toFixed(1)}%</span>
              <span>Drawdown: {metrics.drawdown_pct.toFixed(1)}%</span>
              <span>{metrics.days_remaining}d left</span>
            </div>
          </div>
        )}

        {challenge.status === 'FAILED' && challenge.failure_reason && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="text-xs text-red">
              Failed: {challenge.failure_reason.replace(/_/g, ' ')}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

export default function ChallengeListPage() {
  const { data: challenges, isLoading, error } = useChallenges();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load challenges</p>
        <p className="text-xs text-ink-3">Check that the API server is running.</p>
      </div>
    );
  }

  const all = challenges ?? [];
  const active = all.filter((c) => c.status === 'ACTIVE');
  const pending = all.filter((c) => c.status === 'PENDING');
  const completed = all.filter((c) => ['PASSED', 'FAILED', 'EXPIRED'].includes(c.status));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Challenges</h1>
          <p className="text-sm text-ink-3 mt-0.5">
            Prove your strategy edge and earn real rewards
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/challenges/leaderboard">
            <Button variant="ghost" size="sm">
              <Medal size={14} className="mr-1.5" />
              Leaderboard
            </Button>
          </Link>
          <Link to="/challenges/new">
            <Button size="sm">
              <Plus size={14} className="mr-1.5" />
              New Challenge
            </Button>
          </Link>
        </div>
      </div>

      {all.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <Trophy size={40} className="mx-auto text-ink-4 mb-3" />
          <h2 className="text-lg font-bold mb-1">No challenges yet</h2>
          <p className="text-sm text-ink-3 mb-4 max-w-md mx-auto">
            Start a challenge to prove your strategy can hit profit targets while staying within risk limits.
          </p>
          <Link to="/challenges/new">
            <Button>
              <Plus size={14} className="mr-1.5" />
              Start Your First Challenge
            </Button>
          </Link>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-ink-3 uppercase tracking-wider mb-3">Active</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {active.map((c) => <ChallengeCard key={c.id} challenge={c} />)}
              </div>
            </div>
          )}

          {pending.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-ink-3 uppercase tracking-wider mb-3">Pending Activation</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pending.map((c) => <ChallengeCard key={c.id} challenge={c} />)}
              </div>
            </div>
          )}

          {completed.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-ink-3 uppercase tracking-wider mb-3">Completed</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {completed.map((c) => <ChallengeCard key={c.id} challenge={c} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
